/**
 * Penalties service — READ-ONLY access to `penalties`.
 *
 * A penalty is a money deduction applied to a mentor's payout when they exceed
 * their free scheduling allowance (or manually by an admin):
 *   {
 *     classId, requestId|null, mentorId, studentId, bucketKey,
 *     type: 'mentor_cancel_over_quota'|'reschedule_expired_over_quota'|'admin_manual',
 *     amount, currency: 'INR', reason,
 *     status: 'applied'|'waived',
 *     appliedBy: 'system'|'admin', appliedByName,
 *     waivedBy|null, waivedByName, waivedReason, waivedAt|null,
 *     auditTrail: [...], createdAt, updatedAt
 *   }
 *
 * Written only by the scheduling Cloud Functions and the admin callables
 * (waive/adjust) — separate from sessionLogs.adminPenalty so system penalties
 * keep provenance and stay individually waivable. The payout engine
 * (features/payouts/rules.js) folds `applied` penalties into each session row.
 */
import firestoreService, { where, orderBy } from './firestore.service';

const COLLECTION = 'penalties';

export const PENALTY_STATUS = Object.freeze({
  APPLIED: 'applied',
  WAIVED: 'waived',
});

export const PENALTY_TYPE_LABEL = Object.freeze({
  mentor_cancel_over_quota: 'Cancellation over free limit',
  reschedule_over_quota: 'Reschedule over free allowance',
  reschedule_expired_over_quota: 'Reschedule limit exceeded (request lapsed)',
  admin_manual: 'Manual (admin)',
});

export const penaltiesService = {
  /** All penalties for one mentor, newest first. */
  subscribeByMentor(mentorId, onData, onError) {
    return firestoreService.subscribeCollection(
      COLLECTION,
      [where('mentorId', '==', mentorId), orderBy('createdAt', 'desc')],
      onData,
      onError
    );
  },

  /** Penalties attached to one class, newest first. */
  subscribeByClass(classId, onData, onError) {
    return firestoreService.subscribeCollection(
      COLLECTION,
      [where('classId', '==', classId), orderBy('createdAt', 'desc')],
      onData,
      onError
    );
  },

  /** Admin: every penalty platform-wide, newest first. */
  subscribeAll(onData, onError) {
    return firestoreService.subscribeCollection(
      COLLECTION,
      [orderBy('createdAt', 'desc')],
      onData,
      onError
    );
  },

  get(id) {
    return firestoreService.getDocument(COLLECTION, id);
  },
};

export default penaltiesService;
